import { useQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import gql from 'graphql-tag';
import { findIndex } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import { forwardRef } from 'react';
import { useImperativeHandle } from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import React from 'react';
import ItemTable from '../../components/ItemTable';
import useMutationFHG from '../../fhg/components/data/useMutationFHG';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import FHGTypography from '../../fhg/components/Typography';
import ExtraFieldEdit from './ExtraFieldEdit';

const EXTRA_FIELD_INFO_FRAGMENT = gql`
   fragment extraFieldInfo on OperatorExtraField {
      id
      operatorId
      label
      fieldType
      isRequired
      options
      allowExplanation
      sortOrder
   }
`;

const EXTRA_FIELD_BY_OPERATOR_QUERY = gql`
   query getExtraFieldsByOperator($operatorId: Int!) {
      extraFields: operatorExtraField_ByOperatorId(operatorId: $operatorId) {
         ...extraFieldInfo
      }
   }
   ${EXTRA_FIELD_INFO_FRAGMENT}
`;

const EXTRA_FIELD_CREATE = gql`
   mutation extraFieldCreate($operatorId: Int!, $label: String!, $fieldType: String!, $isRequired: Boolean,
      $options: [String], $allowExplanation: Boolean) {
      extraField: operatorExtraField_Create(operatorExtraField: {operatorId: $operatorId, label: $label,
         fieldType: $fieldType, isRequired: $isRequired, options: $options, allowExplanation: $allowExplanation}) {
         ...extraFieldInfo
      }
   }
   ${EXTRA_FIELD_INFO_FRAGMENT}
`;

const EXTRA_FIELD_UPDATE = gql`
   mutation extraFieldUpdate($id: Int!, $label: String!, $fieldType: String!, $isRequired: Boolean,
      $options: [String], $allowExplanation: Boolean) {
      extraField: operatorExtraField_Update(operatorExtraFieldId: $id, operatorExtraField: {label: $label,
         fieldType: $fieldType, isRequired: $isRequired, options: $options, allowExplanation: $allowExplanation}) {
         ...extraFieldInfo
      }
   }
   ${EXTRA_FIELD_INFO_FRAGMENT}
`;

const EXTRA_FIELD_DELETE = gql`
   mutation extraFieldDelete($id: Int!) {
      operatorExtraField_Delete(operatorExtraFieldId: $id)
   }
`;

const EXTRA_FIELD_REORDER = gql`
   mutation extraFieldReorder($operatorId: Int!, $ids: [Int]!) {
      operatorExtraField_Reorder(operatorId: $operatorId, operatorExtraFieldIds: $ids)
   }
`;

/**
 * Add/edit/delete/reorder table for an operator's extra fields. These fields are shown on the mobile New Test page
 * and their values appear in the CSV and PDF reports. Mutations run immediately and the list refetches - the parent
 * only needs the exposed refetch() (used when switching to this tab on the operator page).
 */
const ExtraFieldTable = forwardRef(function ExtraFieldTable({ operatorId }, ref) {
   const variables = useMemo(() => ({ operatorId }), [operatorId]);

   const { data, loading, error, refetch } = useQuery(EXTRA_FIELD_BY_OPERATOR_QUERY,
      { variables, skip: !operatorId });

   const refetchQueries = useCallback(() => ([{ query: EXTRA_FIELD_BY_OPERATOR_QUERY, variables }]), [variables]);

   const [extraFieldCreate] = useMutationFHG(EXTRA_FIELD_CREATE, { refetchQueries });
   const [extraFieldUpdate] = useMutationFHG(EXTRA_FIELD_UPDATE, { refetchQueries });
   const [extraFieldDelete] = useMutationFHG(EXTRA_FIELD_DELETE, { refetchQueries });
   const [extraFieldReorder] = useMutationFHG(EXTRA_FIELD_REORDER, { refetchQueries });

   const [extraFieldId, setExtraFieldId] = useState();
   const [extraFieldValue, setExtraFieldValue] = useState();
   const [showEdit, setShowEdit] = useState(false);
   const [pendingDelete, setPendingDelete] = useState();
   const [isSaving, setIsSaving] = useState(false);

   useImperativeHandle(ref, () => ({
      refetch: () => refetch(),
   }), [refetch]);

   const extraFields = useMemo(() => {
      const rows = [...(data?.extraFields || [])];
      return rows.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));
   }, [data]);

   const selectedIndex = findIndex(extraFields, { id: extraFieldId });

   const handleSelect = useCallback((extraField) => {
      setExtraFieldValue(extraField);
      setExtraFieldId(extraField?.id);
   }, []);

   const handleAdd = useCallback(() => {
      setExtraFieldValue({
         id: uniqueId('new-'), label: '', fieldType: 'text', isRequired: false, options: [], allowExplanation: false,
      });
      setShowEdit(true);
   }, []);

   const handleEdit = useCallback(() => {
      setShowEdit(true);
   }, []);

   const handleEditCompleted = useCallback(async (updatedValue) => {
      setShowEdit(false);
      setIsSaving(true);
      try {
         const isNew = findIndex(extraFields, { id: updatedValue.id }) < 0;
         const fieldVariables = {
            label: updatedValue.label,
            fieldType: updatedValue.fieldType,
            isRequired: !!updatedValue.isRequired,
            options: updatedValue.fieldType === 'dropdown' ?
               (updatedValue.options || []).map((option) => (option || '').trim()).filter((option) => option) : null,
            allowExplanation: updatedValue.fieldType === 'yesno' && !!updatedValue.allowExplanation,
         };
         if (isNew) {
            await extraFieldCreate({ variables: { operatorId, ...fieldVariables } });
         } else {
            await extraFieldUpdate({ variables: { id: updatedValue.id, ...fieldVariables } });
         }
         setExtraFieldId(undefined);
         setExtraFieldValue(undefined);
      } finally {
         setIsSaving(false);
      }
   }, [extraFields, extraFieldCreate, extraFieldUpdate, operatorId]);

   const handleMove = useCallback(async (offset) => {
      const target = selectedIndex + offset;
      if (selectedIndex < 0 || target < 0 || target >= extraFields.length) {
         return;
      }
      const ids = extraFields.map((field) => field.id);
      [ids[selectedIndex], ids[target]] = [ids[target], ids[selectedIndex]];
      setIsSaving(true);
      try {
         await extraFieldReorder({ variables: { operatorId, ids } });
      } finally {
         setIsSaving(false);
      }
   }, [extraFields, extraFieldReorder, operatorId, selectedIndex]);

   const handleDeleteConfirm = useCallback(async () => {
      setIsSaving(true);
      try {
         await extraFieldDelete({ variables: { id: pendingDelete.id } });
         setPendingDelete(undefined);
         setExtraFieldId(undefined);
         setExtraFieldValue(undefined);
      } finally {
         setIsSaving(false);
      }
   }, [extraFieldDelete, pendingDelete]);

   const columns = useMemo(() => ([
      { Header: <FHGTypography id={'extraField.order.column'} />, accessor: 'sortOrder' },
      { Header: <FHGTypography id={'extraField.label.column'} />, accessor: 'label' },
      {
         Header: <FHGTypography id={'extraField.type.column'} />,
         accessor: 'fieldType',
         Cell: ({ value }) => <FHGTypography id={`extraField.type.${value}`} />,
      }, {
         Header: <FHGTypography id={'extraField.required.column'} />,
         accessor: 'isRequired',
         Cell: ({ value }) => (value ? 'Yes' : 'No'),
      }, {
         Header: <FHGTypography id={'extraField.details.column'} />,
         accessor: 'options',
         Cell: ({ row }) => {
            const field = row.original;
            if (field.fieldType === 'dropdown') {
               return (field.options || []).join(', ');
            }
            if (field.fieldType === 'yesno') {
               return field.allowExplanation ? 'With explanation' : '';
            }
            return '';
         },
      },
   ]), []);

   if (error) {
      return <DataLoadingAndErrors error={error} isLoading={false} />;
   }

   return (
      <>
         <DataLoadingAndErrors error={error} isLoading={loading || isSaving} />
         <ItemTable titleKey={'extraField.title'} columns={columns} items={extraFields}
            onSelect={handleSelect} selectId={extraFieldId} showSearchTextField={false}>
            <Button color='primary' onClick={handleAdd} style={{ marginLeft: 8 }}>
               <FHGTypography noWrap id={'extraField.add.button'} />
            </Button>
            <Button color='primary' onClick={handleEdit} style={{ marginLeft: 8 }} disabled={!extraFieldId}>
               <FHGTypography noWrap id={'extraField.edit.button'} disabled={!extraFieldId} />
            </Button>
            <Button color='primary' onClick={() => setPendingDelete(extraFieldValue)} style={{ marginLeft: 8 }}
               disabled={!extraFieldId}>
               <FHGTypography noWrap id={'extraField.delete.button'} disabled={!extraFieldId} />
            </Button>
            <Button color='primary' onClick={() => handleMove(-1)} style={{ marginLeft: 8 }}
               disabled={selectedIndex <= 0 || isSaving}>
               <FHGTypography noWrap id={'extraField.moveUp.button'} />
            </Button>
            <Button color='primary' onClick={() => handleMove(1)} style={{ marginLeft: 8 }}
               disabled={selectedIndex < 0 || selectedIndex >= extraFields.length - 1 || isSaving}>
               <FHGTypography noWrap id={'extraField.moveDown.button'} />
            </Button>
         </ItemTable>
         {showEdit && (
            <ExtraFieldEdit
               extraFieldValue={extraFieldValue}
               onSubmit={handleEditCompleted}
               onClose={() => setShowEdit(false)}
            />
         )}
         {pendingDelete && (
            <ModalDialog
               open={true}
               titleKey={'extraField.delete.title'}
               messageKey={'extraField.delete.message'}
               submitKey={'delete.button'}
               submitColor={'error'}
               onSubmit={handleDeleteConfirm}
               onClose={() => setPendingDelete(undefined)}
               isSaving={isSaving}
               isEnabled={!isSaving}
               maxWidth={'sm'}
               fullWidth
            />
         )}
      </>
   );
});

export default ExtraFieldTable;
